import { useState, useCallback } from "react";
import { Lock, Upload, Download, Trash2, FileText, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is this PDF password protection free?",
    answer: "Yes, completely free with no signup. Protect unlimited PDFs with passwords."
  },
  {
    question: "What's the difference between user and owner password?",
    answer: "User password is needed to open the PDF. Owner password controls permissions like printing, copying, and editing even after the PDF is opened."
  },
  {
    question: "Can I control what users can do with the PDF?",
    answer: "Yes, you can set permissions to allow or deny printing, copying text, and modifying the document."
  },
  {
    question: "How secure is the encryption?",
    answer: "We use industry-standard PDF encryption. The strength depends on your password - use a strong, unique password."
  },
  {
    question: "Can I remove the password later?",
    answer: "Yes, use our Unlock PDF tool to remove password protection if you know the password."
  }
];

const PdfProtectPage = () => {
  const [file, setFile] = useState(null);
  const [userPassword, setUserPassword] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [permissions, setPermissions] = useState({
    print: true,
    copy: true,
    modify: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleProtect = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (!userPassword) {
      toast.error("Please enter a password");
      return;
    }

    if (userPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_password", userPassword);
    if (ownerPassword) {
      formData.append("owner_password", ownerPassword);
    }
    
    // Build permissions string
    const permList = [];
    if (permissions.print) permList.push("print");
    if (permissions.copy) permList.push("copy");
    if (permissions.modify) permList.push("modify");
    formData.append("permissions", permList.length > 0 ? permList.join(",") : "none");

    try {
      const response = await axios.post(`${API}/pdf-protect`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Protection failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "protected.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setUserPassword("");
    setOwnerPassword("");
  };

  return (
    <>
      <SEOHead
        title="Protect PDF with Password Free Online | Secure PDF"
        description="Add password protection to PDF files. Set permissions for printing, copying, editing. Free online PDF encryption. No signup required."
        canonicalPath="/pdf-protect"
        keywords="protect PDF, password protect PDF, encrypt PDF, secure PDF, PDF password free, lock PDF"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Protect PDF with Password Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Add password protection to your PDF documents. Control who can open, print, copy, or edit your files. 
              No signup required.
            </p>
          </div>

        {/* Upload & Options */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Password Options */}
            <div className="border border-border p-6 space-y-6">
              <div>
                <Label>Password to open PDF *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    placeholder="Enter password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Users will need this password to open the PDF
                </p>
              </div>

              <div>
                <Label>Owner Password (optional)</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  placeholder="Leave empty to use same as user password"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Different password for full document control
                </p>
              </div>

              <div>
                <Label className="mb-3 block">Permissions</Label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="print"
                      checked={permissions.print}
                      onCheckedChange={(checked) => 
                        setPermissions(p => ({ ...p, print: checked }))
                      }
                    />
                    <Label htmlFor="print" className="cursor-pointer">Allow printing</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="copy"
                      checked={permissions.copy}
                      onCheckedChange={(checked) => 
                        setPermissions(p => ({ ...p, copy: checked }))
                      }
                    />
                    <Label htmlFor="copy" className="cursor-pointer">Allow copying text</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="modify"
                      checked={permissions.modify}
                      onCheckedChange={(checked) => 
                        setPermissions(p => ({ ...p, modify: checked }))
                      }
                    />
                    <Label htmlFor="modify" className="cursor-pointer">Allow modifications</Label>
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleProtect}
              disabled={!file || !userPassword || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="protect-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Protecting...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Protect PDF
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Protected
              </h2>
              <p className="text-muted-foreground mb-2">{result.message}</p>
              <p className="text-sm text-muted-foreground mb-6">
                Remember your password: <code className="bg-secondary px-2 py-1">{userPassword}</code>
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={handleReset} className="rounded-sm">
                  <Trash2 className="w-4 h-4 mr-2" /> Protect Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-protect" content={toolContent["pdf-protect"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-protect" />
      </div>
    </div>
    </>
  );
};

export default PdfProtectPage;
